import { useCallback } from 'react';
import dayjs, { Dayjs } from 'dayjs';

import { isAssignedTeamTask, useAuthContext, useUpdateTeamTask } from '../data-access';
import type { AssignedTeamTask } from '../data-access';
import { trackEventMixpanel } from '../utils';

export interface MoveAssignedTeamTaskParams {
  task: AssignedTeamTask;
  targetDate: Dayjs;
  sortIndex?: number;
}

export const useMoveAssignedTeamTask = () => {
  const { user } = useAuthContext();
  const { updateTeamTask, isUpdatingTeamTask } = useUpdateTeamTask();

  const handleMoveAssignedTeamTask = useCallback(
    async ({ task, targetDate, sortIndex }: MoveAssignedTeamTaskParams) => {
      if (!user.userId || !isAssignedTeamTask(task)) {
        return;
      }

      const hasDueDateTime = !!task.dueDateTime?.datetime;

      let targetDueDatetime = targetDate;

      if (hasDueDateTime) {
        const prevDueDate = dayjs(task.dueDateTime.datetime);
        targetDueDatetime = targetDate.hour(prevDueDate.hour()).minute(prevDueDate.minute());
      }

      const targetDateString = targetDate.format('YYYY-MM-DD');
      const updatedTask: AssignedTeamTask = {
        ...task,
        dueDateTime: {
          ...(hasDueDateTime ? { datetime: targetDueDatetime.toISOString() } : {}),
          date: targetDateString,
          timezone: dayjs.tz.guess(),
        },
        ...(sortIndex !== undefined ? { sortIndex } : {}),
      };

      await updateTeamTask({
        teamId: task.assignedTeamTask.team.teamId,
        boardId: task.assignedTeamTask.project.boardId,
        taskId: task.taskId,
        body: {
          name: task.name,
          desc: task.desc || '',
          dueDateTime: {
            ...(hasDueDateTime ? { datetime: targetDueDatetime.toISOString() } : {}),
            date: targetDateString,
            timezone: dayjs.tz.guess(),
          },
          duration: task.duration,
          priority: task.priority,
        },
        assignedTeamTaskMoveParam: { updatedTask, newDate: targetDateString },
      });

      trackEventMixpanel('move_date_of_task');
    },
    [updateTeamTask, user.userId]
  );

  return {
    handleMoveAssignedTeamTask,
    isMovingAssignedTeamTask: isUpdatingTeamTask,
  };
};
